/**
 * Единый вход за адресами игровой статики (KAN-907) и справочником с CDN (KAN-903).
 *
 * Правила, которые здесь собраны в одном месте, раньше были размазаны по
 * champion-image.ts, rank-image.ts, role-paths.ts и сборкам URL в карточках:
 *
 * - **Чемпионы** — с нашего CDN по манифесту, с фолбэком на прокси бэкенда.
 * - **Ранги** — локальные webp из `public/ranks`. На CDN их выносить НЕ надо:
 *   84 КБ с того же origin, что и страница, приходят мгновенно, а отдельный хост
 *   добавил бы DNS, TLS и лишнее соединение. Единообразие тут проиграло бы скорости.
 * - **Роли** — инлайновый SVG в бандле по той же причине: сетевого запроса нет вовсе.
 *
 * Если в будущем возникнет желание «унифицировать» ранги и роли, унеся их на CDN, —
 * это сделает их медленнее. Именно поэтому причина записана здесь, а не в кане.
 */

/**
 * Адрес CDN читаем при каждом обращении, а не один раз при импорте модуля.
 *
 * Next подставляет `NEXT_PUBLIC_*` на сборке, но модуль импортируется и там, где
 * переменной ещё нет (тесты, серверный рендер до подстановки). Зафиксировав
 * значение на импорте, мы получали пустую строку навсегда — и манифест молча
 * перестал бы использоваться, свалившись на тяжёлый путь.
 */
function cdnBase(): string {
  return process.env["NEXT_PUBLIC_CDN_URL"]?.trim().replace(/\/$/, "") ?? "";
}

/** Чемпион из манифеста CDN. Имена — официальные, из Data Dragon (KAN-895). */
export interface GameStaticChampion {
  id: string;
  key: string;
  name: string;
  nameEn: string;
  tags?: string[] | null;
  /** Готовые адреса по видам: «icon», «portrait». Относительные, от корня CDN. */
  images?: Record<string, string> | null;
}

export interface GameStaticManifest {
  version: string;
  generatedAtUtc?: string;
  champions: GameStaticChampion[];
}

/** Есть ли вообще CDN в этом окружении (локальная разработка часто без него). */
export const cdnConfigured = cdnBase().length > 0;

/** Адрес манифеста или null, если CDN не настроен. */
export function getManifestUrl(): string | null {
  const base = cdnBase();
  return base ? `${base}/game/latest/manifest.json` : null;
}

/**
 * Абсолютный адрес картинки из манифеста. Манифест хранит путь от корня CDN, а не
 * полный URL: домен CDN отличается между окружениями, и зашивать его в файл на диске
 * значит получить прод-ссылки на тест-стенде.
 */
export function championImageFromManifest(
  champion: GameStaticChampion | undefined,
  kind: "icon" | "portrait",
): string | null {
  const path = champion?.images?.[kind];
  const base = cdnBase();
  if (!path || !base) return null;
  return `${base}${path}`;
}

/**
 * Адреса картинок из манифеста, по id чемпиона (KAN-907).
 *
 * Держим реестр в модуле, а не тянем манифест в каждый компонент: адреса нужны
 * карточкам турниров, бордам матчей и комнате драфта, и просить их у React-хука
 * из функции-сборщика URL нельзя — она синхронная и вызывается из разметки.
 *
 * Пусто до первой загрузки манифеста: тогда работают прежние пути (CDN по
 * соглашению об именах, затем прокси бэкенда), и это не ошибка, а нормальный
 * порядок — картинка появится и без манифеста, просто тяжелее.
 */
const manifestImages = new Map<string, Record<string, string>>();

/** Запоминает адреса из манифеста. Повторный вызов замещает — патч сменился. */
export function registerManifestImages(
  champions: readonly { id: string; images?: Record<string, string> | null }[],
): void {
  manifestImages.clear();
  for (const champ of champions) {
    if (champ.id && champ.images) manifestImages.set(champ.id, champ.images);
  }
}

/**
 * Готовый адрес из манифеста или null, если манифеста ещё нет либо в нём нет
 * такого вида картинки. Вызывающий обязан иметь фолбэк — см. champion-image.ts.
 */
export function manifestImage(championId: string, kind: string): string | null {
  const path = manifestImages.get(championId)?.[kind];
  const base = cdnBase();
  if (!path || !base) return null;
  return `${base}${path}`;
}

/** Сколько чемпионов в реестре — для диагностики и тестов. */
export function manifestImageCount(): number {
  return manifestImages.size;
}

/**
 * Забирает манифест. Возвращает null, если CDN не настроен или файла нет — вызывающий
 * в этом случае идёт за справочником в бэкенд. Пустой манифест тоже считаем отсутствием:
 * комната без чемпионов хуже, чем лишний запрос к ручке.
 */
export async function fetchGameStaticManifest(
  signal?: AbortSignal,
): Promise<GameStaticManifest | null> {
  const url = getManifestUrl();
  if (!url) return null;
  try {
    const res = await fetch(url, { signal });
    if (!res.ok) return null;
    const data = (await res.json()) as GameStaticManifest;
    return data?.champions?.length ? data : null;
  } catch {
    // CDN недоступен, блокировщик, оффлайн — это не ошибка страницы, а повод
    // сходить в бэкенд.
    return null;
  }
}
